<template>
  <Form ref="formState" class="reservation-travellers">
    <div v-if="showRemainingTimeNotice" class="alert-success">
      Rezervovali sme Vám {{ numberOfSeats() }} v tomto termíne. Teraz máte
      {{ remainingTimeInMinutes }} minút na dokončenie rezervácie. <br />
      Po tomto limite miesta uvoľníme ďalším záujemcom.
    </div>

    <div
      v-for="(traveller, index) in state.travellers"
      :key="`traveller-${index}`"
      class="reservation-box margin-lg"
      :class="{ 'margin-lg': index !== state.travellers.length - 1 }"
    >
      <div class="header">
        <BartoIcon icon="reservation-traveller-1" />

        <h2>Cestujúci {{ index + 1 }}</h2>
      </div>

      <div class="row">
        <InputField
          v-model="traveller.first_name"
          label="Meno"
          :name="'first_name' + index"
          :rules="{ required: true }"
          class="col-6 col-md-3"
        />

        <InputField
          v-model="traveller.last_name"
          :rules="{ required: true }"
          label="Priezvisko"
          :name="'last_name' + index"
          class="col-6 col-md-3"
        />

        <DateField
          v-model="traveller.birthdate"
          label="Dátum narodenia"
          :name="'birthdate' + index"
          :max="dateValidation"
          :rules="{ required: true, before: dateValidation, date_format: 'YYYY-MM-DD' }"
          class="col-6 col-md-3"
        />

        <InputField
          v-if="addressRequired"
          v-model="traveller.id_card"
          :rules="{ required: true }"
          label="Číslo OP"
          :name="'id_card' + index"
          class="col-6 col-md-3"
        />

        <div v-if="addressRequired" class="relative col-6 col-md-3">
          <div
            v-if="determineVisibilityOfPreFillAddress(index, traveller)"
            class="pre-fill-address"
          >
            <div class="heading">Použiť adresu</div>

            <template v-for="address in uniqueAddresses">
              <div
                v-if="address !== concatTravellerAddress(traveller)"
                :key="`address-${address}`"
                class="address-item"
                @mousedown="useAddress(address.split('|'), index)"
                @touchstart="useAddress(address.split('|'), index)"
              >
                {{ getAddressString(address.split('|')) }}
              </div>
            </template>
          </div>

          <InputField
            v-model="traveller.address_street"
            :rules="{ required: true }"
            label="Ulica a číslo domu"
            :name="'address_street' + index"
            @focus="addPreFillAddress(index)"
            @blur="removePreFillAddress(index)"
          />
        </div>

        <InputField
          v-if="addressRequired"
          v-model="traveller.address_city"
          :rules="{ required: true }"
          label="Mesto"
          :name="'address_city' + index"
          class="col-6 col-md-3"
          @focus="addPreFillAddress(index)"
          @blur="removePreFillAddress(index)"
        />

        <InputField
          v-if="addressRequired"
          v-model="traveller.address_post_code"
          :rules="{ required: true }"
          label="PSČ"
          :name="'address_post_code' + index"
          class="col-6 col-md-3"
          @focus="addPreFillAddress(index)"
          @blur="removePreFillAddress(index)"
        />

        <SelectField
          v-if="addressRequired"
          v-model="traveller.address_country"
          label="Krajina"
          :name="'address_country' + index"
          :options="computedCountries"
          :rules="{ required: true }"
          class="col-6 col-md-3"
        />

        <div class="col-6 col-md-3">
          <SelectField
            v-model="traveller.pickup_place"
            label="Nástupné miesto"
            :name="'pickup_place' + index"
            :options="computedPickupPlaces"
            expand-on-focus
            :rules="{ required: true }"
          />
        </div>
      </div>
    </div>

    <reservation-box-controls
      back-button-text="Späť na výber počtu cestujúcich"
      continue-button-text="Pokračovať"
      @return="onReturn"
      @continue="onContinue"
    />
  </Form>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue';
import type { FormContext } from 'vee-validate';
import { Form } from 'vee-validate';
import reservationBoxControls from '../components/ReservationBoxControls.vue';
import InputField from '../components/input/InputField.vue';
import DateField from '../components/input/DateField.vue';
import SelectField from '../components/input/Select.vue';
import { useReservation } from '../modules/useReservation';
import { useReservationTime } from '../modules/useReservationTime';
import type { PickupPlace, Traveller } from '../types';
import { pushRoute, Route } from '../router';
import { useLocalTravellers } from '../modules/useLocalTravellers';
import { useDummyData } from '../modules/useDummyData';
import { tracking } from '../modules/analytics/analytics';
import { useReservationTracking } from '../modules/useReservationTracking';
import BartoIcon from '../components/BartoIcon.vue';
import { usePrice } from '../modules/usePrice.ts';

const { setTravellersData } = useReservation();
const { localTravellersRef } = useLocalTravellers();
const { reservationTimeState, getTimeUntilReservationEndsInMinutes } = useReservationTime();
const addressRequired = window.RESERVATION_STORE.travellerAddressRequired;
const { showRemainingTimeNotice } = reservationTimeState;
const remainingTimeInMinutes = getTimeUntilReservationEndsInMinutes();
const { trackPageView } = tracking();
const { trackTripEvent } = useReservationTracking();
const { dateValidation, pickupPlaces, countries } = window.RESERVATION_STORE;

const { dummyDataState, getDummyTraveller } = useDummyData();
const { formattedPrice } = usePrice();

const createPickupPlaceItem = (place: PickupPlace) => {
  return {
    label: `${place.name} ${place.price ? '+' + formattedPrice(place.price) : ''}`,
    code: place.id,
  };
};

const computedPickupPlaces = computed(() => {
  if (pickupPlaces.length > 1) {
    return [
      {
        label: '',
        code: undefined,
      },
      ...pickupPlaces.map((place) => createPickupPlaceItem(place)),
    ] as any;
  }

  return pickupPlaces.map((place) => createPickupPlaceItem(place));
});

const computedCountries = computed((): { code: string | undefined; label: string }[] => [
  {
    label: '',
    code: undefined,
  },
  ...Object.entries(countries).map(([code, countryName]) => ({ label: countryName, code })),
]);

const formState = ref<FormContext | null>(null);

const state = reactive<{
  travellers: Traveller[];
  visibleAddressFiller: number[];
}>({
  travellers: localTravellersRef.value ?? [],
  visibleAddressFiller: [],
});

const concatTravellerAddress = (traveller: Traveller) =>
  `${traveller.address_street?.trim() ?? ''}|${traveller.address_post_code?.trim() ?? ''}|${
    traveller.address_city?.trim() ?? ''
  }|${traveller.address_country?.trim() ?? ''}`;

const uniqueAddresses = computed(() => {
  const uniqueAddresses = new Set(
    state.travellers
      .filter(
        (traveller) =>
          traveller.address_street &&
          traveller.address_street.length > 0 &&
          traveller.address_post_code &&
          traveller.address_post_code.length > 0 &&
          traveller.address_city &&
          traveller.address_city.length > 0
      )
      .map((traveller) => concatTravellerAddress(traveller))
  );

  return [...uniqueAddresses];
});

const numberOfSeats = () => {
  if (state.travellers.length === 1) {
    return '1 miesto';
  }
  if (state.travellers.length > 1 && state.travellers.length < 5) {
    return `${state.travellers.length} miesta`;
  }
  return `${state.travellers.length} miest`;
};

const onReturn = () => {
  pushRoute(Route.NumberOfTravellers);
};

const onContinue = async () => {
  const validationResult = await formState.value?.validate();

  if (!validationResult?.valid) {
    return;
  }

  try {
    await setTravellersData(state.travellers);
    trackTripEvent({ event: 'reservation_add_travellers_data', value: 0 });
    pushRoute(Route.ServicesData);
  } catch (e) {
    throw e;
  }
};

const addPreFillAddress = (index: number) => {
  state.visibleAddressFiller.push(index);
};

const removePreFillAddress = (index: number) => {
  state.visibleAddressFiller.splice(state.visibleAddressFiller.indexOf(index), 1);
};

const useAddress = (address: string[], index: number) => {
  state.travellers[index] = {
    ...state.travellers[index],
    address_street: address[0],
    address_post_code: address[1],
    address_city: address[2],
    address_country: address[3],
  };
};

const determineVisibilityOfPreFillAddress = (index: number, traveller: Traveller) => {
  if (state.visibleAddressFiller.includes(index)) {
    if (
      uniqueAddresses.value.length === 1 &&
      uniqueAddresses.value[0] !== concatTravellerAddress(traveller)
    ) {
      return true;
    }
    if (uniqueAddresses.value.length > 1) {
      return true;
    }
  }

  return false;
};

const getAddressString = (address: string[]) =>
  `${address[0]}, ${address[1]} ${address[2]}, ${window.RESERVATION_STORE.countries[address[3]]}`;

onMounted(() => {
  if (dummyDataState.enabled) {
    state.travellers.forEach((item, index) => {
      if (!item.first_name) {
        state.travellers[index] = { ...state.travellers[index], ...getDummyTraveller(index) };
      }
    });
  }

  trackPageView({
    screen: 'RESERVATION_TRAVELLERS_DATA',
  });
});
</script>
